<template lang="pug">
  .template-exchange
    .module-header
      .module-title {{ moduleData.mainTitle }}
      //- 倒计时：有截止时间则展示，大于1天"XX天截止"，小于1天"剩HH:mm:ss"
      CountDown.header-sub(
        v-if="taskDeadLineValue > 0"
        :targetTime="taskDeadLineValue"
        :format="isDeadLineMoreThanOneDay ? 'DD' : 'HH:mm:ss'"
        :prefixText="isDeadLineMoreThanOneDay ? '' : '剩'"
        :suffixText="isDeadLineMoreThanOneDay ? '天后截止' : ''"
        :prefixStyle="{ fontSize: '0.12rem' }"
        :suffixStyle="{ fontSize: '0.12rem' }"
        @finish="$emit('expired')"
      )

    //- 进度区：进度条（左） + 商品卡片（右）
    .progress-row(v-if="goodsCost > 0")
      //- 进度条区域（左侧）
      .progress-area
        .progress-text {{ progressText }}
        .exchange-progress(:class="{ 'is-full': canClaim }")
          .exchange-progress-text
            span {{ displayPoints }}
            span /{{ goodsCost }}
          .exchange-progress-bar(:style="progressBarStyle")
            .exchange-progress-bar-text
              span {{ displayPoints }}
              span.cost-text /{{ goodsCost }}
      //- 商品卡片（右侧）+ 悬浮「立即领」按钮
      //- 暂时写死模板一、二 type=exchange
      .goods-card-wrap(
        ref="goodsCardWrap"
        v-platform-expose="trackProductExpose")
        NewcomerGoodsCard(
          :goodsInfo="baseGoodsInfo"
          type="exchange"
          :canClaim="canClaim"
          :claimBtnExposeInfo="claimBtnBusinessInfo"
          @click="handleGoodsClick"
        )

    //- 任务列表
    NewcomerTaskList(
      v-if="shouldShowTaskList"
      :tasks="tasks"
      :totalReward="totalTaskReward"
      @refreshGuide="() => {this.$emit('refreshGuide')}"
    )

    //- 兑换确认弹窗
    ExchangePopup(
      v-if="showExchangePopup"
      :visible="showExchangePopup"
      :exchangeInfo="exchangePopupInfo"
      :points="points"
      :popupSource="1"
      :useV2Api="true"
      @close="showExchangePopup = false"
      @success="showExchangePopup = false"
      @bounty="$emit('bounty')"
      @showExchangeSuccessPopup="handleShowExchangeSuccessPopup"
      @refreshModule="$emit('expired')"
    )

    //- 兑换成功弹窗
    ExchangeSuccessPopup(
      v-if="exchangeSuccessPopupVisible"
      :visible="exchangeSuccessPopupVisible"
      :goodsInfo="exchangeSuccessGoodsInfo"
      @close="handleExchangeSuccessClose"
      @showInfoClick="handleExchangeGoodsInfoClick"
    )
</template>

<script>
import { CountDown } from '@hb/platformui-h5';
import NewcomerGoodsCard from './components/goods-card';
import NewcomerTaskList from './components/newcomer-task-list';
import { welfareGoCoupon } from '../../../utils';
import SPM from '../../../common/spm';

export default {
  name: 'TemplateExchange',
  components: {
    CountDown,
    NewcomerGoodsCard,
    NewcomerTaskList,
    // 懒加载弹窗组件
    ExchangePopup: () => import('../exchange-popup.vue'),
    ExchangeSuccessPopup: () => import('../exchange-success-popup.vue'),
  },
  props: {
    moduleData: {
      type: Object,
      default: () => ({}),
    },
    points: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      // 兑换确认弹窗
      showExchangePopup: false,
      exchangePopupInfo: {},
      // 兑换成功弹窗
      exchangeSuccessPopupVisible: false,
      exchangeSuccessGoodsInfo: {},
    };
  },
  computed: {
    guideData() {
      return this.moduleData.newComerGuideDTO || {};
    },
    goodsCost() {
      return this.guideData.bonusCount || 0;
    },
    tasks() {
      return this.guideData.taskShowDTOS || [];
    },
    canClaim() {
      return this.points >= this.goodsCost && this.goodsCost > 0;
    },
    totalTaskReward() {
      return this.tasks.reduce((sum, t) => sum + (t.offerWorth || 0), 0);
    },
    displayPoints() {
      return Math.min(this.points, this.goodsCost);
    },
    taskDeadLineValue() {
      return this.guideData.taskDeadLine || 0;
    },
    // 截止时间是否大于 1 天
    isDeadLineMoreThanOneDay() {
      return this.taskDeadLineValue > 0
        && (this.taskDeadLineValue - Date.now()) > 86400 * 1000;
    },
    // 任务列表展示
    shouldShowTaskList() {
      if (!this.tasks.length) return false;
      const allDoneAndClaimed = this.tasks.every(
        (t) => t.taskStatus === 'FINISHED' && t.actionStep === 2,
      );
      if (allDoneAndClaimed) return false;
      return true;
    },
    baseGoodsInfo() {
      const {
        imageUrl = '',
        conversionMoney = 0,
        skuId = '',
        virtualType = '',
        promotionInfos = [],
        salePrice = 0,
        itemId = '',
        saleNum = 0,
      } = this.guideData;
      return {
        imageUrl,
        cost: this.goodsCost,
        price: conversionMoney,
        skuId,
        virtualType,
        promotionInfos,
        salePrice,
        itemId,
        saleNum,
      };
    },
    // 商品名称，用于埋点 sku_name 参数
    skuName() {
      return this.guideData.skuName || '';
    },
    // 任务剩余天数，用于埋点 remainingDays 参数
    remainingDays() {
      if (!this.taskDeadLineValue) return '';
      const diff = this.taskDeadLineValue - Date.now();
      if (diff <= 0) return '已到期';
      const days = Math.floor(diff / (86400 * 1000));
      return days >= 1 ? `${days}天` : '小于1天';
    },
    // v-expose 配置：目标商品曝光
    trackProductExpose() {
      return {
        handler: () => {
          SPM.moduleExpose({
            moduleId: 'platform_newcomerProduct',
            contentId: 'platform_newcomerProduct',
            businessInfo: {
              bonusAmount: String(this.points),
              exchangebonus: String(this.goodsCost),
              skuId: String(this.guideData.skuId || ''),
              sku_name: this.skuName,
              task_name: this.guideData.taskName || '',
              taskid: String(this.guideData.taskCode || ''),
              remainingDays: this.remainingDays,
            },
          });
        },
      };
    },
    // 进度条样式
    progressBarStyle() {
      if (this.points === 0 || !this.goodsCost) {
        return { width: '0.14rem' };
      }
      const ratio = Math.min(this.points / this.goodsCost, 1);
      const width = (0.14 + ratio * 2.32).toFixed(2);
      return { width: `${width}rem` };
    },
    progressText() {
      const remain = Math.max(this.goodsCost - this.points, 0);
      return remain > 0 ? `再攒${remain}币可领` : '已攒够，快去领取吧';
    },
    // 「立即领」按钮曝光/点击共用的 businessInfo
    claimBtnBusinessInfo() {
      return {
        exchangebonus: String(this.goodsCost),
        skuId: String(this.guideData.skuId || ''),
        sku_name: this.skuName,
        taskid: String(this.guideData.taskCode || ''),
      };
    },
  },
  mounted() {
    SPM.moduleExpose({
      moduleId: 'platform_newcomerModule',
      contentId: 'platform_newcomerModule',
      businessInfo: {
        skuId: String(this.guideData.skuId || ''),
        sku_name: this.skuName,
        taskid: String(this.guideData.taskCode || ''),
        remainingDays: this.remainingDays,
        tasksAmount: String(this.tasks.length),
      },
    });
  },

  methods: {
    // 商品卡片点击：上报立即领按钮埋点 + 弹出兑换确认弹窗
    handleGoodsClick() {
      SPM.clickButton({
        buttonName: 'platform_productGet',
        businessInfo: this.claimBtnBusinessInfo,
      });
      const {
        imageUrl, cost, price, skuId, virtualType,
        promotionInfos, salePrice, itemId, saleNum,
      } = this.baseGoodsInfo;
      this.exchangePopupInfo = {
        title: this.skuName || '',
        amount: cost,
        cost,
        price,
        skuId,
        virtualType,
        salePrice,
        promotionInfos,
        itemId,
        image: imageUrl,
        saleNum,
      };
      this.showExchangePopup = true;
    },
    handleExchangeGoodsInfoClick() {
      this.handleExchangeSuccessClose();
      welfareGoCoupon();
      SPM.clickButton({
        buttonName: 'Welfare_skuduihuanpop_chakan',
        businessInfo: {},
      });
    },
    // 兑换成功弹窗展示
    handleShowExchangeSuccessPopup(goodsInfo) {
      this.showExchangePopup = false;
      this.exchangeSuccessGoodsInfo = goodsInfo;
      this.exchangeSuccessPopupVisible = true;
    },
    // 兑换成功弹窗关闭
    handleExchangeSuccessClose() {
      this.exchangeSuccessPopupVisible = false;
      this.$emit('expired');
    },
  },
};
</script>

<style lang="less" scoped>
.template-exchange {
  width: 3.51rem;
  border-radius: 0.16rem;
  background: url('https://m.hellobike.com/7a7a72f607.png') no-repeat center / cover;
  position: relative;
  overflow: hidden;
  padding: 0.14rem 0.12rem 0.13rem;

  .module-header {
    display: flex;
    align-items: flex-start;
    margin-bottom: 0.1rem;
    position: relative;
    z-index: 1;

    .module-title {
      font-size: 0.15rem;
      font-weight: 600;
      font-family: 'PingFang SC', sans-serif;
      line-height: 1.4;
      color: #242729;
      max-width: 2.43rem;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    .header-sub {
      font-size: 0.12rem;
      font-family: 'PingFang SC', sans-serif;
      font-weight: 400;
      color: #879099;
      margin-left: 0.06rem;
      white-space: nowrap;
      margin-top: 0.01rem;
      flex-shrink: 0;
    }
  }

  .progress-row {
    display: flex;
    align-items: flex-start;
    position: relative;
    z-index: 1;

    .progress-area {
      flex: 1;
      min-width: 0;
      position: relative;

      .progress-text {
        font-size: 0.12rem;
        color: #979797;
        margin-top: 0.06rem;
        margin-bottom: 0.08rem;
      }

      .exchange-progress {
        overflow: hidden;
        position: relative;
        width: 2.46rem;
        height: 0.14rem;
        background-color: #f0f3f5;
        border-radius: 0.135rem 0 0 0.135rem;

        .exchange-progress-text {
          position: absolute;
          top: 0;
          left: 1.4rem;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          width: 1rem;
          height: 100%;
          font-size: 0.1rem;
          line-height: normal;
          color: #bcc4cc;
        }

        .exchange-progress-bar {
          position: relative;
          overflow: hidden;
          width: 0;
          height: 0.14rem;
          background: linear-gradient(90deg, #ff7753 0%, #fe4043 100%);
          border-radius: 0.135rem;
          transition: width 0.5s ease;

          .exchange-progress-bar-text {
            position: absolute;
            top: 0;
            left: 1.4rem;
            display: flex;
            align-items: center;
            justify-content: flex-end;
            width: 1rem;
            height: 100%;
            font-size: 0.1rem;
            line-height: normal;
            color: #fff;

            .cost-text {
              color: rgba(255, 255, 255, 0.6);
            }
          }
        }

        &.is-full {
          border-radius: 0.135rem 0 0 0.135rem;

          .exchange-progress-bar {
            border-radius: 0.135rem 0 0 0.135rem;
          }
        }
      }
    }

    .goods-card-wrap {
      flex-shrink: 0;
      position: relative;
      z-index: 1;
    }
  }
}
</style>
